import { ArrowRight } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { authApi } from "@/services/auth";

interface CTASectionProps {
  className?: string;
}

export default function CTASection({ className = "" }: CTASectionProps) {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const handleGoogleSignup = async () => {
    try {
      setIsGoogleLoading(true);
      const response = await authApi.getGoogleUrl();
      window.location.href = response.url;
    } catch (error) {
      console.error("Google signup error:", error);
      toast({
        title: "Lỗi đăng nhập",
        description: "Không thể kết nối với Google. Vui lòng thử lại.",
        variant: "destructive",
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <section
      className={`px-4 py-16 bg-gradient-to-r from-[#E06448]/10 to-[#215E60]/10 ${className}`}
    >
      <div className="container mx-auto max-w-4xl text-center">
        <div className="bg-[#F4E7DA]/5 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-[#F4E7DA]/20">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-midnight-blue">
            Bắt Đầu Ngay Hôm Nay – Để Ngày Mai Luôn An Tâm
          </h2>
          <p className="text-lg mb-8 text-midnight-blue/90 max-w-2xl mx-auto">
            Chỉ 30 giây để bảo vệ gia đình tốt hơn. Tham gia Locuno để con tự do
            khám phá, mẹ luôn an lòng.
            <span className="text-burnt-orange font-semibold">
              {" "}
              Miễn phí & an toàn tuyệt đối.
            </span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <Button
              onClick={handleGoogleSignup}
              disabled={isGoogleLoading}
              size="lg"
              className="bg-white hover:bg-gray-50 text-gray-900 font-semibold px-8 py-4 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-gray-200"
            >
              {isGoogleLoading ? (
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
                  <span>Đang chuyển hướng...</span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <img
                    src="/icons/google.svg"
                    alt="Google"
                    className="w-6 h-6"
                  />
                  <span>Đăng Ký Với Google</span>
                  <ArrowRight className="ml-1 w-5 h-5" />
                </div>
              )}
            </Button>
          </div>

          <p className="text-sm text-midnight-blue/70 mb-6">
            ✓ Miễn phí &nbsp;&nbsp; ✓ Đăng ký 1 click với Google &nbsp;&nbsp; ✓
            Bảo mật tuyệt đối
          </p>

          {/* App Store Links */}
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-midnight-blue/70">
              Hoặc tải ứng dụng di động:
            </p>
            <div className="flex gap-4">
              <a
                href="https://apps.apple.com/vn/app/locuno/id6753800628"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <img
                  src="/images/appstore.png"
                  alt="Download on App Store"
                  className="h-12 w-auto"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.locunoapp"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <img
                  src="/images/playstore.png"
                  alt="Get it on Google Play"
                  className="h-12 w-auto"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
